/**
 * Chargeblast tracker: purchase IP + customer email for chargeback deflection
 * (Order Insight / Digital Receipts). Phase 1, Stripe only: Stripe stays the
 * source of the transaction, the tracker only adds IP and email to it.
 * Docs: https://docs.chargeblast.com/guides/tracker-snippet
 *
 * How the tracker works (cdn.cgb.la/v1/scripts.js, ~1 KB): loading it does
 * nothing. `Chargeblast.identify(email)` reads `data-account` off the
 * `#chargeblast-script` tag, gets the public IP from api.ipify.org and POSTs
 * {account, ip, email} to api.chargeblast.com/track. So the IP is captured only
 * on identify, and the tag must keep that id. `Chargeblast` is a top-level
 * `const` of that script, not a `window` property: it is reached as a bare
 * global name. identify() catches its own errors; we wrap it anyway, so a
 * blocked or broken tracker never touches checkout.
 *
 * When /track goes out:
 * - the email becomes known (`identifyChargeblast`): once per email per page load;
 * - every Stripe charge (`identifyChargeblastPurchase`): paywall, upsale, one-time.
 *   One paywall payment makes two charges (intro + subscription) a few seconds
 *   apart, so purchase calls with the same email within PURCHASE_MERGE_MS are one.
 *
 * Off by default. App turns it on when the flow pays through Stripe
 * (`setChargeblastEnabled`); an empty REACT_APP_CHARGEBLAST_ACCOUNT_ID keeps it
 * off as well.
 */

declare const Chargeblast: { identify: (email: string) => Promise<void> | void } | undefined;

const SCRIPT_ID = 'chargeblast-script';
const SCRIPT_SRC = 'https://cdn.cgb.la/v1/scripts.js';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PURCHASE_MERGE_MS = 10000;

let enabled = false;
/** Emails waiting for the tracker script (or for the flow to turn out Stripe). */
let queue: string[] = [];
/** Lowercased emails already sent as "email known". */
const knownSent = new Set<string>();
let lastEmail: string | undefined;
let lastPurchase: { email: string; at: number } | undefined;
let emailSource: (() => unknown) | undefined;

const getAccount = () => process.env.REACT_APP_CHARGEBLAST_ACCOUNT_ID || '';

const getTracker = () =>
  typeof Chargeblast !== 'undefined' && typeof Chargeblast?.identify === 'function'
    ? Chargeblast
    : undefined;

/** A real address only: anonymous users have no email, and a half-typed one is not sent. */
export const isChargeblastEmail = (email: unknown): email is string =>
  typeof email === 'string' && EMAIL_RE.test(email.trim());

const flush = () => {
  if (!enabled || !queue.length) return;

  const tracker = getTracker();
  if (!tracker) {
    // onload calls flush again
    loadChargeblastTracker();
    return;
  }

  const emails = queue;
  queue = [];
  emails.forEach((email) => {
    try {
      Promise.resolve(tracker.identify(email)).catch(() => {});
    } catch (e) {
      // never let the tracker break the payment flow
    }
  });
};

const send = (email: string) => {
  queue.push(email);
  flush();
};

/** Inject the tracker once. Called after the first screen (the funnel's App.tsx) and on demand. */
export const loadChargeblastTracker = () => {
  const account = getAccount();
  if (!enabled || !account || typeof document === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = SCRIPT_SRC;
  script.setAttribute('data-account', account);
  script.onload = flush;
  document.head.appendChild(script);
};

/** Stripe flows only. Turning it on sends what arrived before. */
export const setChargeblastEnabled = (on: boolean) => {
  enabled = on && Boolean(getAccount());
  flush();
};

/**
 * Where a purchase looks for the email when the payment call has none: the
 * app's own store (after a reload on an upsale the module has forgotten it).
 */
export const setChargeblastEmailSource = (getEmail: () => unknown) => {
  emailSource = getEmail;
};

/**
 * The email became known: submitted on an email screen or popup, sent to the
 * payment backend, returned by a wallet. The same email is sent once per page
 * load, a corrected one is sent again.
 */
export const identifyChargeblast = (email: unknown) => {
  if (!isChargeblastEmail(email)) return;
  const clean = email.trim();
  lastEmail = clean;

  const key = clean.toLowerCase();
  if (knownSent.has(key)) return;
  knownSent.add(key);
  send(clean);
};

/**
 * A Stripe charge is going out (subscription, intro, upsale, one-time). Sent
 * on every purchase with the payment's email, else the last known one, else the
 * app store's; nothing when no email is known yet (deferred-email flow before
 * email-after-payment).
 */
export const identifyChargeblastPurchase = (email?: unknown) => {
  const candidates = [email, lastEmail, emailSource?.()];
  const found = candidates.find(isChargeblastEmail);
  if (!found) return;
  const clean = found.trim();
  lastEmail = clean;

  const key = clean.toLowerCase();
  // a later "email known" for this address adds nothing
  knownSent.add(key);

  const now = Date.now();
  if (lastPurchase && lastPurchase.email === key && now - lastPurchase.at < PURCHASE_MERGE_MS) return;
  lastPurchase = { email: key, at: now };
  send(clean);
};
